import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { getConsentFromDocument, setConsentCookie } from '#app/utils/consent.ts'

export function CookieConsentBanner() {
	const [showBanner, setShowBanner] = useState(false)

	useEffect(() => {
		if (getConsentFromDocument() === null) {
			setShowBanner(true)
		}
	}, [])

	if (!showBanner) return null

	function handleAccept() {
		setConsentCookie('granted')
		setShowBanner(false)
		if (ENV.POSTHOG_API_KEY) {
			void import('#app/utils/analytics.client.tsx').then(({ init }) => init())
		}
	}

	function handleDecline() {
		setConsentCookie('denied')
		setShowBanner(false)
	}

	return (
		<div
			role="dialog"
			aria-label="Cookie consent"
			// A bottom-fixed bar covers whatever the page happens to end on - here
			// that was the hero's trust ledger, so the proof was occluded by the
			// exact artifact this product audits. Publishing the rendered height
			// lets the page reserve the space instead (see tailwind.css). React 19
			// runs the returned cleanup when the banner unmounts on accept/decline.
			// Measured a frame late, on purpose. Reading `offsetHeight` inside the
			// commit forces a synchronous layout, and the write that follows dirties
			// the whole document because `body`'s padding reads this property - a
			// performance trace attributed 216ms of forced reflow to this one line.
			// By the next frame layout has already settled, so the read is free and
			// the reserved space still lands before the user can scroll to the end.
			ref={(node) => {
				if (!node) return
				const root = document.documentElement
				const frame = requestAnimationFrame(() => {
					root.style.setProperty(
						'--cookie-banner-height',
						`${node.offsetHeight}px`,
					)
				})
				return () => {
					cancelAnimationFrame(frame)
					root.style.removeProperty('--cookie-banner-height')
				}
			}}
			className="animate-in slide-in-from-bottom pointer-events-none fixed inset-x-0 bottom-0 z-50 p-4"
		>
			{/* Anchored bottom-right from `sm` up rather than centred at 768px wide.
			    Centred, it sat directly over the hero's trust ledger - the left
			    track of the page - and clipped the figures the hero is there to
			    show. The right corner is empty at every width above `sm`.
			    Below `sm` it stays full-width and stacks: there is no corner to
			    retreat to, and the buttons need honest touch targets. */}
			{/* gap-4, not gap-3: the Privacy Policy link is an 18px inline target
			    and Decline sat 13px below it, so a 24px circle centred on either
			    intersected the other. 2.5.8's inline exception covers it, but this
			    is the one banner where a mis-tap has legal meaning. */}
			<div className="bg-card pointer-events-auto flex flex-col gap-4 rounded-lg border p-4 shadow-lg sm:ml-auto sm:max-w-md">
				<p className="text-muted-foreground text-sm">
					We use cookies to improve your experience.{' '}
					<Link to="/privacy" className="hover:text-foreground underline">
						Privacy Policy
					</Link>
				</p>
				<div className="flex shrink-0 gap-2 sm:justify-end [&>*]:flex-1 sm:[&>*]:flex-none">
					<Button variant="outline" size="sm" onClick={handleDecline}>
						Decline
					</Button>
					<Button variant="primary" size="sm" onClick={handleAccept}>
						Accept
					</Button>
				</div>
			</div>
		</div>
	)
}
